import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Calendar, Clock, RefreshCw, GripVertical, User, MapPin, Car, AlertTriangle, Check, GripHorizontal, Phone, Bell } from "lucide-react";
import RdvDetailModal from "./RdvDetailModal";
import RappelDetailModal from "./RappelDetailModal";
import getApiUrl from '../../utils/getApiUrl';

const API = getApiUrl();

// Configuration
const SLOT_HEIGHT = 30; // Hauteur d'un créneau de 30 minutes en pixels
const DEFAULT_DURATIONS = {
  R1: 90, // 1h30 par défaut pour R1
  R2: 60, // 1h par défaut pour R2
  RAPPEL: 15, // 15 min pour un rappel
  R2: 60, // 1h par défaut pour R2
};

// Créneaux horaires de 8h à 21h (par 30 min)
const TIME_SLOTS = [];
for (let h = 8; h <= 21; h++) {
  TIME_SLOTS.push(`${h.toString().padStart(2, '0')}:00`);
  if (h < 21) TIME_SLOTS.push(`${h.toString().padStart(2, '0')}:30`);
}

const JOURS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

// Convertir heure en minutes depuis minuit
const timeToMinutes = (time) => {
  if (!time) return 0;
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

// Convertir minutes en heure HH:MM
const minutesToTime = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
};

// Générer une heure par défaut basée sur l'index du RDV
const getDefaultTime = (rdvIndex, type) => {
  const baseHours = type === "R1" ? [9, 10, 11, 14, 15, 16, 17] : [9, 11, 14, 16];
  const hour = baseHours[rdvIndex % baseHours.length];
  return `${hour.toString().padStart(2, '0')}:00`;
};

// Calculer les positions horizontales pour les RDV qui se chevauchent
const calculateOverlapPositions = (rdvList) => {
  if (!rdvList || rdvList.length === 0) return [];
  
  // Trier par heure de début
  const sorted = [...rdvList].sort((a, b) => timeToMinutes(a.heure) - timeToMinutes(b.heure));
  
  // Calculer fin de chaque RDV
  const rdvsWithEnd = sorted.map(r => ({
    ...r,
    startMinutes: timeToMinutes(r.heure),
    endMinutes: timeToMinutes(r.heure) + (r.duration || DEFAULT_DURATIONS[r.type] || 60)
  }));
  
  // Trouver les groupes de RDV qui se chevauchent
  const groups = [];
  let currentGroup = [];
  let currentGroupEnd = 0;
  
  for (const rdv of rdvsWithEnd) {
    if (currentGroup.length === 0 || rdv.startMinutes < currentGroupEnd) {
      // Ce RDV chevauche le groupe actuel
      currentGroup.push(rdv);
      currentGroupEnd = Math.max(currentGroupEnd, rdv.endMinutes);
    } else {
      // Nouveau groupe
      if (currentGroup.length > 0) groups.push(currentGroup);
      currentGroup = [rdv];
      currentGroupEnd = rdv.endMinutes;
    }
  }
  if (currentGroup.length > 0) groups.push(currentGroup);
  
  // Assigner les colonnes dans chaque groupe
  const result = [];
  for (const group of groups) {
    const columns = []; // Liste des colonnes, chaque colonne contient les RDV assignés
    
    for (const rdv of group) {
      // Trouver la première colonne où ce RDV peut être placé
      let columnIndex = 0;
      while (true) {
        if (!columns[columnIndex]) {
          columns[columnIndex] = [];
        }
        // Vérifier si ce RDV chevauche un RDV déjà dans cette colonne
        const canPlace = columns[columnIndex].every(existing => 
          rdv.startMinutes >= existing.endMinutes || rdv.endMinutes <= existing.startMinutes
        );
        if (canPlace) {
          columns[columnIndex].push(rdv);
          break;
        }
        columnIndex++;
      }
      
      result.push({
        ...rdv,
        columnIndex,
        totalColumns: 0 // Sera mis à jour après
      });
    }
    
    // Mettre à jour le nombre total de colonnes pour le groupe
    const totalCols = columns.length;
    for (const rdv of result) {
      if (group.some(g => g.id === rdv.id)) {
        rdv.totalColumns = totalCols;
      }
    }
  }
  
  return result;
};

// Calculer le temps de trajet estimé entre deux départements
const estimateTravelTime = (dpt1, dpt2) => {
  if (!dpt1 || !dpt2) return null;
  const dptDistances = {
    '75': { '75': 20, '92': 25, '93': 30, '94': 30, '77': 60, '78': 45, '91': 50, '95': 40 },
    '92': { '75': 25, '92': 20, '93': 35, '94': 40, '77': 70, '78': 35, '91': 55, '95': 45 },
    '93': { '75': 30, '92': 35, '93': 20, '94': 35, '77': 50, '78': 55, '91': 55, '95': 35 },
    '94': { '75': 30, '92': 40, '93': 35, '94': 20, '77': 45, '78': 60, '91': 40, '95': 50 },
    '77': { '75': 60, '92': 70, '93': 50, '94': 45, '77': 30, '78': 80, '91': 50, '95': 70 },
    '78': { '75': 45, '92': 35, '93': 55, '94': 60, '77': 80, '78': 25, '91': 50, '95': 50 },
    '91': { '75': 50, '92': 55, '93': 55, '94': 40, '77': 50, '78': 50, '91': 25, '95': 65 },
    '95': { '75': 40, '92': 45, '93': 35, '94': 50, '77': 70, '78': 50, '91': 65, '95': 25 },
  };
  const d1 = String(dpt1).substring(0, 2);
  const d2 = String(dpt2).substring(0, 2);
  return dptDistances[d1]?.[d2] || 45;
};

// Composant Rappel (cliquable pour voir/éditer)
const RappelItem = ({ rappel, slotHeight = SLOT_HEIGHT, onClick, onDragStart, onDragEnd, isDragging = false }) => {
  const duration = 15; // Un rappel = 15 min
  const heightPx = (duration / 30) * slotHeight;
  
  // Vérifier si le rappel est en retard
  const now = new Date();
  const today = now.toISOString().split('T')[0];
  const isOverdue = rappel.date_rappel < today || 
    (rappel.date_rappel === today && rappel.heure_rappel && (() => {
      const [h, m] = rappel.heure_rappel.split(':');
      const rappelTime = new Date(now);
      rappelTime.setHours(parseInt(h), parseInt(m), 0, 0);
      return now > rappelTime;
    })());
  
  const handleDragStart = (e) => {
    e.stopPropagation();
    if (onDragStart) {
      onDragStart(e, rappel);
    }
  };
  
  return (
    <div 
      className={`absolute left-1 right-1 rounded-lg border-2 border-dashed cursor-grab active:cursor-grabbing transition-all hover:scale-[1.02] ${
        isDragging ? 'opacity-50 scale-95' : ''
      } ${
        isOverdue 
          ? 'bg-red-500/30 border-red-400 hover:bg-red-500/40' 
          : 'bg-amber-500/30 border-amber-400 hover:bg-amber-500/40'
      }`}
      style={{ height: `${heightPx}px`, minHeight: '28px', zIndex: 15 }}
      onClick={(e) => { e.stopPropagation(); onClick && onClick(rappel); }}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
      title={`Glisser-déposer pour reprogrammer - ${rappel.prenom} ${rappel.nom}`}
      data-testid={`rappel-planning-${rappel.lead_id}`}
    >
      <div className="flex items-center gap-1 px-2 py-0.5 h-full pointer-events-none">
        <Bell className={`w-3 h-3 flex-shrink-0 ${isOverdue ? 'text-red-400' : 'text-amber-400'}`} />
        <span className={`text-xs font-medium truncate ${isOverdue ? 'text-red-300' : 'text-amber-300'}`}>
          {rappel.prenom} {rappel.nom}
        </span>
        <Phone className={`w-3 h-3 flex-shrink-0 ml-auto ${isOverdue ? 'text-red-400' : 'text-amber-400'}`} />
      </div>
    </div>
  );
};

// Composant RDV avec durée et redimensionnement
const ResizableRdv = ({ rdv, onDragStart, onDragEnd, onResize, onClick, isDragging = false, slotHeight = SLOT_HEIGHT, columnIndex = 0, totalColumns = 1 }) => {
  const [isResizing, setIsResizing] = useState(false);
  const [tempDuration, setTempDuration] = useState(null);
  const startYRef = useRef(0);
  const startDurationRef = useRef(0);
  const clickTimeRef = useRef(0);
  const resizeHandleRef = useRef(null);
  const currentDurationRef = useRef(null); // Ref pour stocker la durée courante pendant le resize
  
  const duration = tempDuration ?? rdv.duration ?? DEFAULT_DURATIONS[rdv.type] ?? 60;
  const heightPx = (duration / 30) * slotHeight;
  
  // Calcul de la largeur et position horizontale pour les chevauchements
  const widthPercent = totalColumns > 1 ? (100 / totalColumns) - 1 : 100;
  const leftPercent = columnIndex * (100 / totalColumns);
  
  const colors = rdv.type === "R1" 
    ? "bg-blue-500/90 border-blue-400 hover:bg-blue-500" 
    : "bg-purple-500/90 border-purple-400 hover:bg-purple-500";
  
  const handleMouseDown = (e) => {
    clickTimeRef.current = Date.now();
  };
  
  const handleClick = (e) => {
    // Ne pas ouvrir si c'est un drag ou resize
    const clickDuration = Date.now() - clickTimeRef.current;
    if (clickDuration < 200 && !isResizing && onClick) {
      e.stopPropagation();
      onClick(rdv);
    }
  };
  
  // Empêcher le drag quand on commence sur la poignée de resize
  const handleDragStart = (e) => {
    // Vérifier si le clic est sur la poignée de resize
    if (resizeHandleRef.current && resizeHandleRef.current.contains(e.target)) {
      e.preventDefault();
      return;
    }
    if (!isResizing && onDragStart) {
      onDragStart(e, rdv);
    }
  };
  
  const handleResizeStart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    console.log("🔧 Resize started for RDV:", rdv.id);
    setIsResizing(true);
    startYRef.current = e.clientY;
    startDurationRef.current = duration;
    currentDurationRef.current = null; // Reset
    
    const handleMouseMove = (moveE) => {
      const deltaY = moveE.clientY - startYRef.current;
      const deltaMinutes = Math.round(deltaY / slotHeight) * 30;
      const newDuration = Math.max(30, Math.min(180, startDurationRef.current + deltaMinutes));
      console.log("🔧 Resizing:", deltaY, "px ->", newDuration, "min");
      currentDurationRef.current = newDuration; // Stocker dans la ref
      setTempDuration(newDuration);
    };
    
    const handleMouseUp = () => {
      const finalDuration = currentDurationRef.current;
      console.log("🔧 Resize ended, finalDuration:", finalDuration);
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      
      // Utiliser la ref au lieu du state
      if (finalDuration && finalDuration !== rdv.duration && onResize) {
        console.log("🔧 Calling onResize with:", finalDuration);
        onResize(rdv, finalDuration);
      }
      setTempDuration(null);
      currentDurationRef.current = null;
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  
  // Formater la durée
  const formatDuration = (mins) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0) return `${m}min`;
    if (m === 0) return `${h}h`;
    return `${h}h${m.toString().padStart(2, '0')}`;
  };
  
  return (
    <div
      draggable={!isResizing}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
      className={`${colors} border-l-4 rounded-lg cursor-pointer active:cursor-grabbing transition-all shadow-lg hover:shadow-xl absolute ${isDragging ? 'opacity-50' : ''} ${isResizing ? 'ring-2 ring-amber-400' : ''}`}
      style={{ 
        height: `${heightPx - 2}px`,
        left: `${leftPercent}%`,
        width: `${widthPercent}%`,
        marginLeft: '2px',
        marginRight: '2px',
        zIndex: 20
      }}
      data-testid={`rdv-${rdv.id}`}
      title="Cliquer pour voir les détails"
    >
      {/* Contenu du RDV */}
      <div className="p-1.5 h-full flex flex-col pointer-events-none">
        <div className="flex items-center gap-1">
          <GripVertical className="w-3 h-3 text-white/60 flex-shrink-0" />
          <span className="font-bold text-white text-xs">{rdv.type}</span>
          <span className="text-white/90 text-xs truncate flex-1">{rdv.client}</span>
        </div>
        
        <div className="flex items-center gap-2 text-white/70 text-[10px] mt-0.5">
          <span className="flex items-center gap-0.5">
            <Clock className="w-2.5 h-2.5" />
            {rdv.heure} - {minutesToTime(timeToMinutes(rdv.heure) + duration)}
          </span>
          <span className="bg-white/20 px-1 rounded">{formatDuration(duration)}</span>
        </div>
        
        {heightPx > 50 && (
          <div className="flex items-center gap-2 text-white/60 text-[10px] mt-auto">
            {rdv.intervenant && <span className="flex items-center gap-0.5"><User className="w-2.5 h-2.5" />{rdv.intervenant}</span>}
            {rdv.departement && <span>• {rdv.departement}</span>}
          </div>
        )}
      </div>
      
      {/* Poignée de redimensionnement en bas - zone de 16px pour faciliter le clic */}
      <div
        ref={resizeHandleRef}
        onMouseDown={handleResizeStart}
        className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-black/30 to-transparent hover:from-amber-500/50 cursor-ns-resize flex items-center justify-center transition-colors z-10"
        title="Glisser pour ajuster la durée"
        data-testid={`resize-handle-${rdv.id}`}
      >
        <GripHorizontal className="w-4 h-4 text-white/80" />
      </div>
    </div>
  );
};

// Composant d'aperçu du drop avec temps de trajet
const DropPreview = ({ time, duration, rdvBefore, rdvAfter, draggedRdv }) => {
  const travelBefore = rdvBefore ? estimateTravelTime(rdvBefore.departement, draggedRdv?.departement) : null;
  const travelAfter = rdvAfter ? estimateTravelTime(draggedRdv?.departement, rdvAfter.departement) : null;
  
  const heightPx = ((duration || 60) / 30) * SLOT_HEIGHT;
  
  return (
    <div 
      className="absolute left-1 right-1 bg-amber-500/30 border-2 border-amber-400 border-dashed rounded-lg flex flex-col items-center justify-center z-10 pointer-events-none"
      style={{ height: `${heightPx}px` }}
    >
      <div className="bg-amber-600 text-white px-2 py-0.5 rounded text-xs font-bold">
        {time} ({Math.round((duration || 60) / 60 * 10) / 10}h)
      </div>
      
      {travelBefore && (
        <div className="absolute -top-5 left-0 right-0 flex justify-center">
          <div className="bg-slate-900/90 text-amber-400 px-1.5 py-0.5 rounded text-[10px] flex items-center gap-1 whitespace-nowrap">
            <Car className="w-2.5 h-2.5" />
            {travelBefore}min depuis {rdvBefore.heure}
          </div>
        </div>
      )}
      
      {travelAfter && (
        <div className="absolute -bottom-5 left-0 right-0 flex justify-center">
          <div className="bg-slate-900/90 text-amber-400 px-1.5 py-0.5 rounded text-[10px] flex items-center gap-1 whitespace-nowrap">
            <Car className="w-2.5 h-2.5" />
            {travelAfter}min vers {rdvAfter.heure}
          </div>
        </div>
      )}
    </div>
  );
};

// Vue Planning Jour
export const DayPlanningView = ({ date, rdvs, rappels = [], onRdvMove, onRdvResize, loading, onViewFullProfile, onRdvDeleted, onSlotClick, hasPendingClient, myPlanningMode, onRappelClick, onRappelUpdated }) => {
  const [draggedRdv, setDraggedRdv] = useState(null);
  const [draggedRappel, setDraggedRappel] = useState(null);
  const [hoverSlot, setHoverSlot] = useState(null);
  const [selectedRdv, setSelectedRdv] = useState(null);
  const [selectedRappel, setSelectedRappel] = useState(null);
  
  const dateKey = useMemo(() => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, [date]);
  
  // RDV du jour avec durées et positions de chevauchement
  const dayRdvs = useMemo(() => {
    const rdvsWithTime = rdvs
      .filter(r => r.date === dateKey)
      .map((r, idx) => ({ 
        ...r, 
        heure: r.heure || getDefaultTime(idx, r.type),
        duration: r.duration || DEFAULT_DURATIONS[r.type] || 60
      }));
    
    // Calculer les positions pour les chevauchements
    return calculateOverlapPositions(rdvsWithTime);
  }, [rdvs, dateKey]);
  
  // Rappels du jour
  const dayRappels = useMemo(() => {
    return (rappels || []).filter(r => r.date_rappel === dateKey);
  }, [rappels, dateKey]);
  
  // Position verticale d'un RDV (en pixels depuis le haut)
  const getTopPosition = (heure) => {
    const minutes = timeToMinutes(heure);
    const startMinutes = 8 * 60; // 8h00
    return ((minutes - startMinutes) / 30) * SLOT_HEIGHT;
  };
  
  // Trouver les RDV adjacents pour un créneau
  const findAdjacentRdvs = useCallback((time) => {
    const timeMinutes = timeToMinutes(time);
    let rdvBefore = null, rdvAfter = null;
    
    for (const r of dayRdvs) {
      if (r.id === draggedRdv?.id) continue;
      const rMinutes = timeToMinutes(r.heure);
      const rEnd = rMinutes + (r.duration || 60);
      
      if (rEnd <= timeMinutes) rdvBefore = r;
      else if (rMinutes > timeMinutes && !rdvAfter) { rdvAfter = r; break; }
    }
    
    return { rdvBefore, rdvAfter };
  }, [dayRdvs, draggedRdv]);
  
  const handleDragStart = (e, rdv) => {
    setDraggedRdv(rdv);
    setDraggedRappel(null);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'rdv', data: rdv }));
  };
  
  const handleRappelDragStart = (e, rappel) => {
    setDraggedRappel(rappel);
    setDraggedRdv(null);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'rappel', data: rappel }));
  };
  
  const handleDragEnd = () => {
    setDraggedRdv(null);
    setDraggedRappel(null);
    setHoverSlot(null);
  };
  
  const handleDragOver = (e, time) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setHoverSlot(time);
  };
  
  const handleDrop = async (e, dropTime) => {
    e.preventDefault();
    
    // Drop d'un RDV
    if (draggedRdv && onRdvMove) {
      await onRdvMove(draggedRdv, dateKey, dropTime);
    }
    
    // Drop d'un rappel - mettre à jour la date et l'heure
    if (draggedRappel && onRappelUpdated) {
      try {
        const API = getApiUrl();
        const resp = await authFetch(`${API}/api/webhook/leads/${draggedRappel.lead_id}/rappel`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            date_rappel: dateKey,
            heure_rappel: dropTime,
            telepro_email: draggedRappel.telepro_rappel,
            notes: draggedRappel.notes
          })
        });
        if (resp.ok) {
          onRappelUpdated();
        }
      } catch (e) {
        console.error('Erreur déplacement rappel:', e);
      }
    }
    
    setDraggedRdv(null);
    setDraggedRappel(null);
    setHoverSlot(null);
  };
  
  const handleResize = (rdv, newDuration) => {
    if (onRdvResize) {
      onRdvResize(rdv, newDuration);
    }
  };
  
  const formatDateTitle = (d) => d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <RefreshCw className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }
  
  // Hauteur totale de la grille (8h à 21h = 26 slots de 30 min)
  const gridHeight = TIME_SLOTS.length * SLOT_HEIGHT;
  
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
      <div className="bg-slate-700/50 px-4 py-3 border-b border-slate-600">
        <h3 className="text-lg font-semibold text-white capitalize">{formatDateTitle(date)}</h3>
        <p className="text-sm text-slate-400">{dayRdvs.length} RDV • R1 = 1h30 par défaut</p>
      </div>
      
      <div className="flex">
        {/* Colonne des heures */}
        <div className="w-16 flex-shrink-0 bg-slate-900/50">
          {TIME_SLOTS.map(time => (
            <div 
              key={time} 
              className={`flex items-center justify-center text-xs border-b border-slate-700/30 ${
                time.endsWith(':00') ? 'text-amber-400 font-medium' : 'text-slate-500'
              }`}
              style={{ height: `${SLOT_HEIGHT}px` }}
            >
              {time}
            </div>
          ))}
        </div>
        
        {/* Zone des RDV (positionnement absolu) */}
        <div className="flex-1 relative" style={{ height: `${gridHeight}px` }}>
          {/* Lignes de grille */}
          {TIME_SLOTS.map((time, idx) => {
            const isHour = time.endsWith(':00');
            const { rdvBefore, rdvAfter } = hoverSlot === time ? findAdjacentRdvs(time) : {};
            const canClick = hasPendingClient || myPlanningMode;
            const isSlotHovered = hoverSlot === time && (draggedRdv || draggedRappel);
            
            return (
              <div
                key={time}
                onDragOver={(e) => handleDragOver(e, time)}
                onDrop={(e) => handleDrop(e, time)}
                onClick={(e) => {
                  // Ne pas déclencher si on a cliqué sur un RDV ou rappel
                  if (e.target === e.currentTarget && onSlotClick && canClick) {
                    onSlotClick(dateKey, time);
                  }
                }}
                className={`absolute left-0 right-0 border-b border-slate-700/30 transition-colors ${
                  isHour ? 'bg-slate-800/30' : 'bg-slate-800/10'
                } ${isSlotHovered ? 'bg-amber-500/10' : ''} ${
                  canClick ? 'cursor-pointer hover:bg-emerald-500/10' : ''
                }`}
                style={{ 
                  top: `${idx * SLOT_HEIGHT}px`, 
                  height: `${SLOT_HEIGHT}px`, 
                  zIndex: 1,
                  pointerEvents: canClick || isSlotHovered ? 'auto' : 'none'
                }}
                data-testid={`slot-day-${time}`}
              >
                {/* Indicateur de clic possible - seulement si canClick */}
                {canClick && (
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      hasPendingClient 
                        ? 'text-emerald-400 bg-emerald-500/20' 
                        : 'text-amber-400 bg-amber-500/20'
                    }`}>
                      {hasPendingClient ? '+ Créer RDV' : '+ Rappel'}
                    </span>
                  </div>
                )}
                {/* Aperçu du drop RDV */}
                {hoverSlot === time && draggedRdv && (
                  <DropPreview 
                    time={time}
                    duration={draggedRdv.duration || DEFAULT_DURATIONS[draggedRdv.type]}
                    rdvBefore={rdvBefore}
                    rdvAfter={rdvAfter}
                    draggedRdv={draggedRdv}
                  />
                )}
                {/* Aperçu du drop rappel */}
                {hoverSlot === time && draggedRappel && (
                  <div className="absolute inset-0 bg-amber-500/30 rounded flex items-center justify-center">
                    <span className="text-xs text-amber-300">Déplacer ici</span>
                  </div>
                )}
              </div>
            );
          })}
          
          {/* RDV positionnés avec gestion des chevauchements - z-20 pour être au-dessus des slots */}
          {dayRdvs.map(rdv => (
            <div 
              key={rdv.id}
              className="absolute left-0 right-0 z-20"
              style={{ top: `${getTopPosition(rdv.heure)}px` }}
            >
              <ResizableRdv
                rdv={rdv}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onResize={handleResize}
                onClick={setSelectedRdv}
                isDragging={draggedRdv?.id === rdv.id}
                columnIndex={rdv.columnIndex || 0}
                totalColumns={rdv.totalColumns || 1}
              />
            </div>
          ))}
          
          {/* Rappels du jour - z-15 pour être en dessous des RDV mais au-dessus des slots */}
          {dayRappels.map((rappel, idx) => (
            <div 
              key={rappel.lead_id || `rappel-${idx}`}
              className="absolute left-0 right-0 z-15"
              style={{ top: `${getTopPosition(rappel.heure_rappel || '09:00')}px` }}
            >
              <RappelItem
                rappel={rappel}
                onClick={() => setSelectedRappel(rappel)}
                onDragStart={handleRappelDragStart}
                onDragEnd={handleDragEnd}
                isDragging={draggedRappel?.lead_id === rappel.lead_id}
              />
            </div>
          ))}
        </div>
      </div>
      
      {/* Légende */}
      <div className="bg-slate-700/30 px-4 py-2 border-t border-slate-600 flex items-center gap-4 text-xs text-slate-400">
        <span className="flex items-center gap-1"><div className="w-3 h-3 bg-blue-500 rounded" /> R1 (1h30)</span>
        <span className="flex items-center gap-1"><div className="w-3 h-3 bg-purple-500 rounded" /> R2 (1h)</span>
        <span className="flex items-center gap-1"><div className="w-3 h-3 bg-amber-500/50 rounded border border-dashed border-amber-400" /> Rappel (clic pour éditer)</span>
        <span className="flex items-center gap-1"><GripHorizontal className="w-3 h-3" /> Glisser pour ajuster</span>
      </div>
      
      {/* Modal détail RDV */}
      {selectedRdv && (
        <RdvDetailModal
          rdv={selectedRdv}
          onClose={() => setSelectedRdv(null)}
          onViewFullProfile={onViewFullProfile}
          onDelete={(rdv) => {
            setSelectedRdv(null);
            if (onRdvDeleted) onRdvDeleted(rdv);
          }}
          onUpdate={() => {
            if (onRdvDeleted) onRdvDeleted();
          }}
        />
      )}
      
      {/* Modal détail Rappel */}
      {selectedRappel && (
        <RappelDetailModal
          rappel={selectedRappel}
          onClose={() => setSelectedRappel(null)}
          onUpdate={() => {
            setSelectedRappel(null);
            if (onRappelUpdated) onRappelUpdated();
          }}
          onDelete={() => {
            setSelectedRappel(null);
            if (onRappelUpdated) onRappelUpdated();
          }}
          onAppeler={onRappelClick}
          onViewClientFiche={onViewFullProfile}
        />
      )}
    </div>
  );
};

// Vue Planning Semaine
export const WeekPlanningView = ({ weekStart, rdvs, rappels = [], onRdvMove, onRdvResize, loading, onViewFullProfile, onRdvDeleted, onSlotClick, hasPendingClient, myPlanningMode, onRappelClick, onRappelUpdated }) => {
  const [draggedRdv, setDraggedRdv] = useState(null);
  const [draggedRappel, setDraggedRappel] = useState(null);
  const [hoverSlot, setHoverSlot] = useState(null);
  const [selectedRdv, setSelectedRdv] = useState(null);
  const [selectedRappel, setSelectedRappel] = useState(null);
  
  // Jours de la semaine
  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      days.push(d);
    }
    return days;
  }, [weekStart]);
  
  const formatDateKey = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  
  // RDV par jour avec durées et positions de chevauchement
  const rdvsByDay = useMemo(() => {
    const map = {};
    weekDays.forEach(d => {
      const dateKey = formatDateKey(d);
      const dayRdvs = rdvs
        .filter(r => r.date === dateKey)
        .map((r, idx) => ({ 
          ...r, 
          heure: r.heure || getDefaultTime(idx, r.type),
          duration: r.duration || DEFAULT_DURATIONS[r.type] || 60
        }));
      
      // Calculer les positions pour les chevauchements
      map[dateKey] = calculateOverlapPositions(dayRdvs);
    });
    return map;
  }, [rdvs, weekDays]);
  
  // Rappels par jour
  const rappelsByDay = useMemo(() => {
    const map = {};
    weekDays.forEach(d => {
      const dateKey = formatDateKey(d);
      map[dateKey] = (rappels || []).filter(r => r.date_rappel === dateKey);
    });
    return map;
  }, [rappels, weekDays]);
  
  // Position verticale d'un RDV
  const getTopPosition = (heure) => {
    const minutes = timeToMinutes(heure);
    const startMinutes = 8 * 60;
    return ((minutes - startMinutes) / 30) * SLOT_HEIGHT;
  };
  
  // Trouver les RDV adjacents
  const findAdjacentRdvs = useCallback((dateKey, time) => {
    const dayRdvs = rdvsByDay[dateKey] || [];
    const timeMinutes = timeToMinutes(time);
    let rdvBefore = null, rdvAfter = null;
    
    for (const r of dayRdvs) {
      if (r.id === draggedRdv?.id) continue;
      const rMinutes = timeToMinutes(r.heure);
      const rEnd = rMinutes + (r.duration || 60);
      
      if (rEnd <= timeMinutes) rdvBefore = r;
      else if (rMinutes > timeMinutes && !rdvAfter) { rdvAfter = r; break; }
    }
    
    return { rdvBefore, rdvAfter };
  }, [rdvsByDay, draggedRdv]);
  
  const handleDragStart = (e, rdv) => {
    setDraggedRdv(rdv);
    setDraggedRappel(null);
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleRappelDragStart = (e, rappel) => {
    setDraggedRappel(rappel);
    setDraggedRdv(null);
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleDragEnd = () => {
    setDraggedRdv(null);
    setDraggedRappel(null);
    setHoverSlot(null);
  };
  
  const handleDragOver = (e, dateKey, time) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setHoverSlot(`${dateKey}-${time}`);
  };
  
  const handleDrop = async (e, dateKey, time) => {
    e.preventDefault();
    
    // Drop d'un RDV
    if (draggedRdv && onRdvMove) {
      await onRdvMove(draggedRdv, dateKey, time);
    }
    
    // Drop d'un rappel
    if (draggedRappel && onRappelUpdated) {
      try {
        const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

        const resp = await authFetch(`${API}/api/webhook/leads/${draggedRappel.lead_id}/rappel`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            date_rappel: dateKey,
            heure_rappel: time,
            telepro_email: draggedRappel.telepro_rappel,
            notes: draggedRappel.notes
          })
        });
        if (resp.ok) {
          onRappelUpdated();
        }
      } catch (e) {
        console.error('Erreur déplacement rappel:', e);
      }
    }
    
    setDraggedRdv(null);
    setDraggedRappel(null);
    setHoverSlot(null);
  };
  
  const handleResize = (rdv, newDuration) => {
    if (onRdvResize) {
      onRdvResize(rdv, newDuration);
    }
  };
  
  const isToday = (d) => d.toDateString() === new Date().toDateString();
  
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <RefreshCw className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }
  
  const gridHeight = TIME_SLOTS.length * SLOT_HEIGHT;
  
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
      {/* Header avec les jours */}
      <div className="flex border-b border-slate-600 sticky top-0 z-20 bg-slate-800">
        <div className="w-14 flex-shrink-0 bg-slate-900/50" />
        {weekDays.map((d, i) => {
          const dateKey = formatDateKey(d);
          const dayRdvs = rdvsByDay[dateKey] || [];
          
          return (
            <div 
              key={i} 
              className={`flex-1 text-center py-2 border-l border-slate-700 ${
                isToday(d) ? 'bg-amber-500/20' : 'bg-slate-700/30'
              }`}
            >
              <div className="text-xs text-slate-400">{JOURS[i]}</div>
              <div className={`text-lg font-bold ${isToday(d) ? 'text-amber-400' : 'text-white'}`}>
                {d.getDate()}
              </div>
              {dayRdvs.length > 0 && (
                <div className="text-xs text-amber-400">{dayRdvs.length} RDV</div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Grille horaire avec scroll */}
      <div className="flex max-h-[600px] overflow-y-auto">
        {/* Colonne des heures */}
        <div className="w-14 flex-shrink-0 bg-slate-900/50 sticky left-0 z-10">
          {TIME_SLOTS.map(time => (
            <div 
              key={time} 
              className={`flex items-center justify-center text-[10px] border-b border-slate-700/30 ${
                time.endsWith(':00') ? 'text-amber-400 font-medium' : 'text-slate-500'
              }`}
              style={{ height: `${SLOT_HEIGHT}px` }}
            >
              {time}
            </div>
          ))}
        </div>
        
        {/* Colonnes des jours */}
        {weekDays.map((d, dayIdx) => {
          const dateKey = formatDateKey(d);
          const dayRdvs = rdvsByDay[dateKey] || [];
          
          return (
            <div 
              key={dayIdx} 
              className="flex-1 border-l border-slate-700 relative"
              style={{ height: `${gridHeight}px`, minWidth: '100px' }}
            >
              {/* Lignes de grille */}
              {TIME_SLOTS.map((time, idx) => {
                const slotKey = `${dateKey}-${time}`;
                const isHour = time.endsWith(':00');
                const isHoveredRdv = hoverSlot === slotKey && draggedRdv;
                const isHoveredRappel = hoverSlot === slotKey && draggedRappel;
                const { rdvBefore, rdvAfter } = isHoveredRdv ? findAdjacentRdvs(dateKey, time) : {};
                const canClick = hasPendingClient || myPlanningMode;
                const isSlotHovered = isHoveredRdv || isHoveredRappel;
                
                return (
                  <div
                    key={time}
                    onDragOver={(e) => handleDragOver(e, dateKey, time)}
                    onDrop={(e) => handleDrop(e, dateKey, time)}
                    onClick={(e) => {
                      // Ne pas déclencher si on a cliqué sur un RDV ou rappel
                      if (e.target === e.currentTarget && onSlotClick && canClick) {
                        console.log('Slot clicked:', dateKey, time);
                        onSlotClick(dateKey, time);
                      }
                    }}
                    className={`absolute left-0 right-0 border-b border-slate-700/30 transition-colors ${
                      isHour ? 'bg-slate-800/30' : 'bg-slate-800/10'
                    } ${isToday(d) ? 'bg-amber-500/5' : ''} ${isSlotHovered ? 'bg-amber-500/20' : ''} ${
                      canClick ? 'cursor-pointer hover:bg-emerald-500/10' : ''
                    }`}
                    style={{ 
                      top: `${idx * SLOT_HEIGHT}px`, 
                      height: `${SLOT_HEIGHT}px`, 
                      zIndex: 1,
                      pointerEvents: canClick || isSlotHovered ? 'auto' : 'none'
                    }}
                    data-testid={`slot-${dateKey}-${time.replace(':', '-')}`}
                  >
                    {/* Indicateur de clic possible */}
                    {canClick && (
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
                        <span className={`text-[10px] px-1 rounded ${
                          hasPendingClient 
                            ? 'text-emerald-400 bg-emerald-500/20' 
                            : 'text-amber-400 bg-amber-500/20'
                        }`}>
                          {hasPendingClient ? '+' : '+ Rappel'}
                        </span>
                      </div>
                    )}
                    {isHoveredRdv && (
                      <DropPreview 
                        time={time}
                        duration={draggedRdv.duration || DEFAULT_DURATIONS[draggedRdv.type]}
                        rdvBefore={rdvBefore}
                        rdvAfter={rdvAfter}
                        draggedRdv={draggedRdv}
                      />
                    )}
                    {isHoveredRappel && (
                      <div className="absolute inset-0 bg-amber-500/30 rounded flex items-center justify-center">
                        <span className="text-[10px] text-amber-300">Déplacer</span>
                      </div>
                    )}
                  </div>
                );
              })}
              
              {/* RDV positionnés avec gestion des chevauchements - z-20 pour être au-dessus des slots */}
              {dayRdvs.map(rdv => (
                <div 
                  key={rdv.id}
                  className="absolute left-0 right-0 z-20"
                  style={{ top: `${getTopPosition(rdv.heure)}px` }}
                >
                  <ResizableRdv
                    rdv={rdv}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onResize={handleResize}
                    onClick={setSelectedRdv}
                    isDragging={draggedRdv?.id === rdv.id}
                    slotHeight={SLOT_HEIGHT}
                    columnIndex={rdv.columnIndex || 0}
                    totalColumns={rdv.totalColumns || 1}
                  />
                </div>
              ))}
              
              {/* Rappels du jour - z-15 pour être en dessous des RDV mais au-dessus des slots */}
              {(rappelsByDay[dateKey] || []).map((rappel, idx) => (
                <div 
                  key={rappel.lead_id || `rappel-${idx}`}
                  className="absolute left-0 right-0 z-15"
                  style={{ top: `${getTopPosition(rappel.heure_rappel || '09:00')}px` }}
                >
                  <RappelItem
                    rappel={rappel}
                    slotHeight={SLOT_HEIGHT}
                    onClick={() => setSelectedRappel(rappel)}
                    onDragStart={handleRappelDragStart}
                    onDragEnd={handleDragEnd}
                    isDragging={draggedRappel?.lead_id === rappel.lead_id}
                  />
                </div>
              ))}
            </div>
          );
        })}
      </div>
      
      {/* Légende */}
      <div className="bg-slate-700/30 px-4 py-2 border-t border-slate-600 flex items-center gap-4 text-xs text-slate-400">
        <span className="flex items-center gap-1"><div className="w-3 h-3 bg-blue-500 rounded" /> R1 (1h30)</span>
        <span className="flex items-center gap-1"><div className="w-3 h-3 bg-purple-500 rounded" /> R2 (1h)</span>
        <span className="flex items-center gap-1"><div className="w-3 h-3 bg-amber-500/50 rounded border border-dashed border-amber-400" /> Rappel (clic pour éditer)</span>
        <span className="flex items-center gap-1"><GripHorizontal className="w-3 h-3" /> Glisser pour ajuster</span>
      </div>
      
      {/* Modal détail RDV */}
      {selectedRdv && (
        <RdvDetailModal
          rdv={selectedRdv}
          onClose={() => setSelectedRdv(null)}
          onViewFullProfile={onViewFullProfile}
          onDelete={(rdv) => {
            setSelectedRdv(null);
            if (onRdvDeleted) onRdvDeleted(rdv);
          }}
          onUpdate={() => {
            if (onRdvDeleted) onRdvDeleted();
          }}
        />
      )}
      
      {/* Modal détail Rappel */}
      {selectedRappel && (
        <RappelDetailModal
          rappel={selectedRappel}
          onClose={() => setSelectedRappel(null)}
          onUpdate={() => {
            setSelectedRappel(null);
            if (onRappelUpdated) onRappelUpdated();
          }}
          onDelete={() => {
            setSelectedRappel(null);
            if (onRappelUpdated) onRappelUpdated();
          }}
          onAppeler={onRappelClick}
          onViewClientFiche={onViewFullProfile}
        />
      )}
    </div>
  );
};

export default { DayPlanningView, WeekPlanningView };
